"use client";

import type { Customer } from "@mw/shared/payload-types";

import * as m from "motion/react-m";
import { useCallback } from "react";
import { useLocale } from "next-intl";

import { resolveCustomerLogos, type LogoConfig } from "@utilities/resolveCustomerLogos";
import { useGeoOverride } from "@utilities/geo/useGeoOverride";

import { LogoSlider } from "./LogoSlider";

type ContactSalesGeoLogosProps = {
  defaultLogos: LogoConfig[];
  overrideCountries: string[];
};

type GeoResponse = {
  props: {
    logos: Array<{ customer: string | Customer; id?: string | null }>;
  } | null;
};

const fadeTransition = { duration: 0.3, ease: "easeInOut" as const };

export function ContactSalesGeoLogos({ defaultLogos, overrideCountries }: ContactSalesGeoLogosProps) {
  const locale = useLocale();

  const fetchOverride = useCallback(async (): Promise<LogoConfig[] | null> => {
    const params = new URLSearchParams({ locale });
    const res = await fetch(`/next/api/contact-sales-geo?${params}`);
    const data: GeoResponse = await res.json();
    if (!data.props?.logos) return null;
    const logos = resolveCustomerLogos(data.props.logos);
    return logos.length >= 5 ? logos : null;
  }, [locale]);

  const { resolved, needsOverride } = useGeoOverride<LogoConfig[]>({
    overrideCountries,
    fetchOverride,
    fallback: defaultLogos,
  });

  if (needsOverride === null || needsOverride === false) {
    return null;
  }

  if (!resolved) return null;

  return (
    <m.div data-geo-resolved initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={fadeTransition}>
      <LogoSlider logos={resolved} />
    </m.div>
  );
}
